import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { ApiError } from '@/services/api';
import { portalApi } from '@/services/portal';
import type {
  CorpoSecao,
  PortalSiteDetalhe,
  SecaoId,
  SecaoPortal,
  SecoesDoSite,
} from '@/types/portal';

export const portalKeys = {
  sites: ['portal', 'sites'] as const,
  site: (id: string) => ['portal', 'site', id] as const,
  manifesto: (id: string) => ['portal', 'manifesto', id] as const,
  conferencia: (id: string) => ['portal', 'conferencia', id] as const,
  municipios: (q: string) => ['portal', 'municipios', q] as const,
};

/** 403 (fora do plano) e 404 (API ausente) são respostas finais: repetir não muda nada. */
function repetir(vezes: number, erro: unknown): boolean {
  if (erro instanceof ApiError && [403, 404].includes(erro.status)) return false;
  return vezes < 1;
}

/** O painel de sites se renova sozinho: o estado muda quando o plugin lê o manifesto. */
const RENOVAR_SITES_MS = 30_000;
const RENOVAR_CONFERENCIA_MS = 60_000;

export function usePortalSites() {
  return useQuery({
    queryKey: portalKeys.sites,
    queryFn: portalApi.sites,
    refetchInterval: RENOVAR_SITES_MS,
    retry: repetir,
  });
}

export function usePortalSite(siteId: string | undefined) {
  return useQuery({
    queryKey: portalKeys.site(siteId ?? ''),
    queryFn: () => portalApi.site(siteId as string),
    enabled: Boolean(siteId),
    retry: repetir,
  });
}

export function useManifesto(siteId: string | undefined, habilitado = true) {
  return useQuery({
    queryKey: portalKeys.manifesto(siteId ?? ''),
    queryFn: () => portalApi.manifesto(siteId as string),
    enabled: Boolean(siteId) && habilitado,
    retry: repetir,
  });
}

export function useConferencia(siteId: string | undefined, habilitado = true) {
  return useQuery({
    queryKey: portalKeys.conferencia(siteId ?? ''),
    queryFn: () => portalApi.conferencia(siteId as string),
    enabled: Boolean(siteId) && habilitado,
    refetchInterval: RENOVAR_CONFERENCIA_MS,
    retry: repetir,
  });
}

/** Busca no catálogo IBGE; só a partir de 2 letras, e o catálogo não muda no meio da sessão. */
export function useMunicipios(q: string) {
  const termo = q.trim();
  return useQuery({
    queryKey: portalKeys.municipios(termo.toLowerCase()),
    queryFn: () => portalApi.municipios(termo),
    enabled: termo.length >= 2,
    staleTime: 60 * 60_000,
    retry: repetir,
  });
}

/** Depois de gravar, a seção nova entra no cache do site e o que depende dela é refeito. */
function aplicarSecaoNoCache(qc: QueryClient, siteId: string, nova: SecaoPortal): void {
  qc.setQueryData<PortalSiteDetalhe>(portalKeys.site(siteId), (atual) =>
    atual ? { ...atual, secoes: { ...atual.secoes, [nova.chave]: nova } as SecoesDoSite } : atual,
  );
  void qc.invalidateQueries({ queryKey: portalKeys.sites });
  void qc.invalidateQueries({ queryKey: portalKeys.manifesto(siteId) });
  void qc.invalidateQueries({ queryKey: portalKeys.conferencia(siteId) });
}

export function useSalvarSecao(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { chave: SecaoId; corpo: CorpoSecao; versao: number }) =>
      portalApi.salvarSecao(siteId, p.chave, p.corpo, p.versao),
    onSuccess: (nova) => aplicarSecaoNoCache(qc, siteId, nova),
  });
}

export function useRestaurarSecao(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { chave: SecaoId; versao: number }) =>
      portalApi.restaurarSecao(siteId, p.chave, p.versao),
    onSuccess: (nova) => aplicarSecaoNoCache(qc, siteId, nova),
  });
}

export function useGerarToken(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => portalApi.gerarToken(siteId),
    // A resposta traz o segredo inteiro, que só pode existir enquanto a tela o
    // mostra. `gcTime: 0` joga o resultado fora do cache da mutação assim que
    // nada mais a observa (o padrão seria guardar por cinco minutos).
    gcTime: 0,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: portalKeys.site(siteId) });
      void qc.invalidateQueries({ queryKey: portalKeys.sites });
    },
  });
}

export function useRevogarToken(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => portalApi.revogarToken(siteId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: portalKeys.site(siteId) });
      void qc.invalidateQueries({ queryKey: portalKeys.sites });
    },
  });
}

export function useAtualizacaoAutomatica(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ligada: boolean) => portalApi.atualizacaoAutomatica(siteId, ligada),
    onSuccess: (detalhe) => {
      qc.setQueryData(portalKeys.site(siteId), detalhe);
      void qc.invalidateQueries({ queryKey: portalKeys.sites });
    },
  });
}
